import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, X, Loader2 } from "lucide-react";

const fmt = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

export const ProspectPicker = ({ value, onChange }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [searched, setSearched] = useState("");

  useEffect(() => {
    if (value || q.trim().length < 2) { setItems([]); return undefined; }
    const t = setTimeout(async () => {
      setBusy(true);
      try { const { data } = await api.get("/prospects", { params: { q: q.trim(), type: "" } }); setItems((data.items || []).slice(0, 8)); }
      catch { setItems([]); }
      finally { setBusy(false); setSearched(q.trim()); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, value]);

  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2" data-testid="prospect-picker-selected">
        <div className="flex-1 min-w-0 text-sm">
          <div className="font-semibold truncate">{value.name}</div>
          <div className="text-xs text-muted-foreground" data-testid="prospect-picker-status">
            {value.status || "New"}{value.lastContacted ? ` · last contacted ${fmt(value.lastContacted)}` : ""}{value.followUpDue ? ` · follow up ${fmt(value.followUpDue)}` : ""}
          </div>
        </div>
        <button type="button" onClick={() => { onChange(null); setQ(""); }} aria-label="Clear prospect" className="text-muted-foreground hover:text-foreground" data-testid="prospect-picker-clear"><X className="w-4 h-4" /></button>
      </div>
    );
  }
  return (
    <div className="relative" data-testid="prospect-picker">
      <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your prospects by name…" className="pl-8" data-testid="prospect-picker-input" />
      {busy && <Loader2 className="w-4 h-4 absolute right-2.5 top-2.5 animate-spin text-primary" />}
      {items.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-popover shadow-xl max-h-60 overflow-y-auto">
          {items.map((p) => (
            <button key={p.id} type="button" onClick={() => onChange(p)} data-testid={`prospect-picker-option-${p.id}`}
              className="w-full text-left px-3 py-2 hover:bg-secondary transition-colors">
              <div className="text-sm font-medium truncate">{p.name}</div>
              <div className="text-[11px] text-muted-foreground truncate">{p.status || "New"} · {p.type}{p.address ? ` · ${p.address}` : ""}</div>
            </button>
          ))}
        </div>
      )}
      {!busy && searched && searched === q.trim() && items.length === 0 && <p className="text-xs text-muted-foreground mt-1" data-testid="prospect-picker-empty">No prospects match. Only prospects in your bike path zones are listed.</p>}
    </div>
  );
};
